
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { TradeCaseStudies } from '../TradeCaseStudies';
import {
  ShieldCheck, Server, Zap, CheckCircle2,
  ShoppingBag, Minus, Plus, Cpu, Calculator, ArrowRight, MessageSquare, X, Check, Loader2, Truck, BarChart3, Globe, Lock
} from 'lucide-react';
import { Page } from '../../types';
import { SmartConsultant } from '../SmartConsultant';
import { LeadFormModal } from '../LeadFormModal';

const SupportTerminal = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      const next = (step + 1) % 4;
      setStep(next);
    }, 3000);
    return () => clearInterval(interval);
  }, [step]);

  return (
    <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700 bg-[#0F172A] mt-2 h-[320px] flex flex-col">
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="ml-4 text-xs font-mono text-gray-400">{t('trade.terminal.log')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
          <span className="text-[10px] font-mono text-emerald-500 uppercase">{t('trade.terminal.status_live')}</span>
        </div>
      </div>
      <div className="p-6 font-mono text-xs md:text-sm leading-loose overflow-hidden relative flex-1 text-slate-300">
        <div className="text-gray-500">[10:05:22] {t('trade.terminal.ticket')}</div>
        <div className="text-white opacity-80 pl-4 border-l border-gray-700 my-2">"{t('trade.terminal.message')}"</div>
        <div className={`transition-opacity duration-500 ${step >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-emerald-400">[10:05:23] {t('trade.terminal.intent')}</div>
        </div>
        <div className={`transition-opacity duration-500 ${step >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-yellow-400 mt-2">[10:05:24] {t('trade.terminal.action')}</div>
        </div>
        <div className={`transition-opacity duration-500 mt-4 ${step >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="bg-emerald-900/20 p-3 rounded border border-emerald-500/20 text-emerald-300">{t('trade.terminal.response')}</div>
        </div>
      </div>
    </div>
  );
};

export const TradePage: React.FC<{ onNavigate?: (page: Page) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const subSectors = [
    { title: t('trade.sectors.items.01.title'), id: "01", desc: t('trade.sectors.items.01.desc'), highlight: t('trade.sectors.items.01.highlight') },
    { title: t('trade.sectors.items.02.title'), id: "02", desc: t('trade.sectors.items.02.desc'), highlight: t('trade.sectors.items.02.highlight') },
    { title: t('trade.sectors.items.03.title'), id: "03", desc: t('trade.sectors.items.03.desc'), highlight: t('trade.sectors.items.03.highlight') },
    { title: t('trade.sectors.items.04.title'), id: "04", desc: t('trade.sectors.items.04.desc'), highlight: t('trade.sectors.items.04.highlight') },
    { title: t('trade.sectors.items.05.title'), id: "05", desc: t('trade.sectors.items.05.desc'), highlight: t('trade.sectors.items.05.highlight') },
    { title: t('trade.sectors.items.06.title'), id: "06", desc: t('trade.sectors.items.06.desc'), highlight: t('trade.sectors.items.06.highlight') },
    { title: t('trade.sectors.items.07.title'), id: "07", desc: t('trade.sectors.items.07.desc'), highlight: t('trade.sectors.items.07.highlight') },
    { title: t('trade.sectors.items.08.title'), id: "08", desc: t('trade.sectors.items.08.desc'), highlight: t('trade.sectors.items.08.highlight'), variant: 'dark' }
  ];

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="pt-4">
              <div className="inline-block px-4 py-2 bg-emerald-50 text-emerald-600 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-emerald-100">{t('trade.hero.tagline')}</div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mb-8">
                {t('trade.hero.title_line1')} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">{t('trade.hero.title_line2')}</span>
              </h1>
              <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-lg">{t('trade.hero.subtitle')}</p>
              <div className="flex flex-wrap gap-4 mb-12">
                <button onClick={() => setIsModalOpen(true)} className="bg-emerald-600 text-white px-6 py-4 font-bold uppercase tracking-widest text-xs hover:bg-emerald-700 transition-all shadow-lg rounded-sm">{t('trade.hero.cta_strategy')}</button>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}><SupportTerminal /></Reveal>
        </div>
      </section>

      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('trade.sectors.title').split(' ')[0]}<br />{t('trade.sectors.title').split(' ')[1]}
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200">
            {subSectors.map((sector, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div
                  onClick={() => setIsModalOpen(true)}
                  className={`group p-12 border-b border-r border-gray-200 transition-all duration-500 min-h-[320px] flex flex-col justify-between relative cursor-pointer overflow-hidden h-full ${sector.variant === 'dark' ? 'bg-[#0B1121] text-white border-gray-800' : 'bg-white hover:bg-emerald-50/30'}`}
                >
                  <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><span className={`${sector.variant === 'dark' ? 'text-clever-teal' : 'text-emerald-600'} text-xs font-bold uppercase tracking-widest`}>{sector.highlight}</span></div>
                  <div className={`text-xs font-mono mb-8 ${sector.variant === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>// {sector.id}</div>
                  <div>
                    <h3 className={`text-3xl font-bold mb-6 group-hover:translate-x-2 transition-transform ${sector.variant === 'dark' ? 'text-white' : 'text-gray-900'}`}>{sector.title}</h3>
                    <p className={`leading-relaxed text-lg ${sector.variant === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{sector.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-40 bg-[#0B1121] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-20">
              <div className="inline-block px-4 py-2 bg-emerald-900/30 text-emerald-300 text-xs font-bold uppercase tracking-[0.2em] mb-6 border border-emerald-500/30">{t('trade.consultant.tagline')}</div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">{t('trade.consultant.title')}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">{t('trade.consultant.subtitle')}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}><SmartConsultant context="trade" title={t('trade.consultant.bot_title')} subtitle={t('trade.consultant.bot_subtitle')} suggestions={t('trade.consultant.suggestions', { returnObjects: true }) as string[]} /></Reveal>
        </div>
      </section>

      <TradeCaseStudies />

      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} sector="Trade" />
    </div>
  );
};
